'use client'

import { Component } from 'react'
import type { ErrorInfo, ReactNode } from 'react'
import * as Sentry from '@sentry/nextjs'

type SentryCapture = (error: Error, context?: Record<string, unknown>) => void

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  traceId: string | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, traceId: null }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    const traceId = crypto.randomUUID().slice(0, 8)
    return { hasError: true, error, traceId }
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[ErrorBoundary]', {
      traceId: this.state.traceId,
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    })

    const captureException = (Sentry as unknown as { captureException?: SentryCapture }).captureException
    captureException?.(error, {
      tags: { traceId: this.state.traceId },
      contexts: { react: { componentStack: errorInfo.componentStack } },
    })
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null, traceId: null })
  }

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="flex min-h-[400px] flex-col items-center justify-center gap-4 p-8 text-center" role="alert">
          <div className="rounded-lg border border-red-200 bg-red-50 p-6 dark:border-red-800 dark:bg-red-900/20">
            <h2 className="text-lg font-semibold text-red-800 dark:text-red-300">
              Ein unerwarteter Fehler ist aufgetreten
            </h2>
            <p className="mt-2 text-sm text-red-600 dark:text-red-400">
              Bitte versuchen Sie es erneut. Falls das Problem weiterhin besteht,
              notieren Sie die Referenz-ID.
            </p>
            {this.state.traceId && (
              <p className="mt-2 font-mono text-xs text-red-400 dark:text-red-500">
                Referenz: {this.state.traceId}
              </p>
            )}
            <button
              onClick={this.handleReset}
              className="mt-4 rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900"
            >
              Erneut versuchen
            </button>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
